/**
 * CMS dashboard.
 *
 * One API call returns every tile, queue and chart, so the dashboard is a single round trip. What
 * is shown depends on the signed-in role: an Editor sees content and review queues, Marketing also
 * sees the lead pipeline, a Content Admin sees applications and audit activity too.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Alert, Badge, Card, EmptyState, Skeleton, Spinner } from '@/components/ui';
import { Icon } from '@/components/site/blocks';
import { LoadError } from '@/components/site/blocks';
import { useAuth } from '@/context/AuthContext';
import { cmsApi } from '@/services/api';
import type { ContentCount } from '@/services/types';

function KpiTile({ label, value, sub, to, tone }: { label: string; value: number | string; sub?: string; to?: string; tone?: 'primary' | 'accent' | 'signal' }) {
  const tones = { primary: 'text-primary', accent: 'text-accent', signal: 'text-signal' } as const;
  const body = (
    <>
      <p className="text-xs font-medium uppercase tracking-wide text-muted">{label}</p>
      <p className={`mt-2 font-display text-3xl font-bold ${tone ? tones[tone] : 'text-ink'}`}>{value}</p>
      {sub && <p className="mt-1 text-xs text-muted">{sub}</p>}
    </>
  );

  if (to) {
    return (
      <Link to={to} className="block rounded-lg border border-line bg-surface p-5 no-underline transition-colors hover:border-primary hover:no-underline">
        {body}
      </Link>
    );
  }
  return <div className="rounded-lg border border-line bg-surface p-5">{body}</div>;
}

/** Minimal inline sparkline. A charting library would be heavier than the value it adds here. */
function Sparkline({ points, label }: { points: { date: string; count: number }[]; label: string }) {
  if (points.length < 2) {
    return <p className="text-sm text-muted">Not enough data yet to plot a trend.</p>;
  }
  const max = Math.max(...points.map((point) => point.count), 1);
  const width = 100;
  const height = 32;
  const path = points
    .map((point, index) => {
      const x = (index / (points.length - 1)) * width;
      const y = height - (point.count / max) * height;
      return `${index === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
  const total = points.reduce((sum, point) => sum + point.count, 0);

  return (
    <figure>
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`${label}: ${total} in the last ${points.length} days`} className="h-10 w-full text-primary">
        <path d={path} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
      <figcaption className="mt-2 text-xs text-muted">
        {total} in the last {points.length} days
      </figcaption>
    </figure>
  );
}

function StatusBar({ counts }: { counts: ContentCount }) {
  const segments = [
    { key: 'published', value: counts.published, className: 'bg-accent' },
    { key: 'approved', value: counts.approved, className: 'bg-primary' },
    { key: 'review', value: counts.review, className: 'bg-signal' },
    { key: 'draft', value: counts.draft, className: 'bg-line-strong' },
  ].filter((segment) => segment.value > 0);

  const total = segments.reduce((sum, segment) => sum + segment.value, 0) || 1;

  return (
    <div className="flex h-1.5 overflow-hidden rounded-full bg-sunken" role="presentation">
      {segments.map((segment) => (
        <span key={segment.key} className={segment.className} style={{ width: `${(segment.value / total) * 100}%` }} />
      ))}
    </div>
  );
}

const CONTENT_LABELS: Record<string, string> = {
  pages: 'Pages',
  products: 'Products',
  solutions: 'Solutions',
  services: 'Services',
  industries: 'Industries',
  case_studies: 'Case studies',
  articles: 'Articles',
  job_openings: 'Job openings',
};

export function DashboardPage() {
  const { user, can } = useAuth();
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ['cms', 'dashboard'], queryFn: cmsApi.dashboard });
  const myContentQuery = useQuery({ queryKey: ['cms', 'my-content'], queryFn: cmsApi.myContent });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-28" />
          ))}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (isError || !data) return <LoadError onRetry={() => void refetch()} />;

  const totals = Object.values(data.content).reduce(
    (accumulator, counts) => ({
      total: accumulator.total + counts.total,
      published: accumulator.published + counts.published,
      draft: accumulator.draft + counts.draft,
      review: accumulator.review + counts.review,
    }),
    { total: 0, published: 0, draft: 0, review: 0 },
  );

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const firstName = user?.full_name.split(' ')[0] ?? '';

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-2xl font-bold text-ink">
          {greeting}
          {firstName && `, ${firstName}`}
        </h1>
        <p className="mt-1 text-sm text-muted">
          Here is the state of the website as of {new Date(data.generated_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}.
        </p>
      </div>

      {data.needs_review.length > 0 && (
        <Alert tone="warning" title="Placeholder content needs your input">
          <p>
            Some records were seeded with placeholder text because the information is not published on the previous
            website. Replacing them is the fastest way to make the site fully yours.
          </p>
          <ul className="mt-3 flex flex-wrap gap-2">
            {data.needs_review.map((item) => (
              <li key={item.model}>
                <Link
                  to={`${item.admin_path}?needs_review=true`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-warning/40 bg-surface px-3 py-1 text-xs font-semibold no-underline hover:no-underline"
                >
                  {item.label}
                  <span className="rounded-full bg-signal-soft px-1.5 text-signal">{item.count}</span>
                </Link>
              </li>
            ))}
          </ul>
        </Alert>
      )}

      <section aria-labelledby="kpi-heading">
        <h2 id="kpi-heading" className="sr-only">
          Key figures
        </h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KpiTile label="Total content" value={totals.total} sub={`${totals.published} published`} />
          <KpiTile label="Drafts" value={totals.draft} sub="Work in progress" tone="signal" />
          <KpiTile label="Awaiting review" value={totals.review} sub="Needs an editor" tone="primary" />
          {can('can_manage_leads') && data.leads ? (
            <KpiTile label="Open leads" value={data.leads.open} sub={`${data.leads.new} new`} to="/admin/leads" tone="accent" />
          ) : (
            <KpiTile label="Media assets" value={data.media.total} sub="In the library" to="/admin/media" />
          )}
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <h2 className="text-base font-semibold text-ink">Content by type</h2>
          <ul className="mt-5 space-y-4">
            {Object.entries(data.content).map(([key, counts]) => (
              <li key={key}>
                <div className="flex items-center justify-between gap-4 text-sm">
                  <Link to={counts.admin_path} className="font-medium no-underline hover:underline">
                    {CONTENT_LABELS[key] ?? key}
                  </Link>
                  <span className="shrink-0 text-muted">
                    <span className="font-semibold text-ink">{counts.published}</span> / {counts.total} published
                  </span>
                </div>
                <div className="mt-2">
                  <StatusBar counts={counts} />
                </div>
              </li>
            ))}
          </ul>
        </Card>

        <Card>
          <h2 className="text-base font-semibold text-ink">Awaiting review</h2>
          {data.review_queue.length === 0 ? (
            <p className="mt-4 text-sm text-muted">Nothing is waiting for review. The queue is clear.</p>
          ) : (
            <ul className="mt-4 divide-y divide-line">
              {data.review_queue.map((item) => (
                <li key={`${item.model}-${item.id}`} className="flex items-center justify-between gap-4 py-3">
                  <div className="min-w-0">
                    <Link to={item.admin_path} className="block truncate text-sm font-medium no-underline hover:underline">
                      {item.title}
                    </Link>
                    <p className="mt-0.5 text-xs text-muted">
                      {CONTENT_LABELS[item.type] ?? item.type} · updated{' '}
                      {new Date(item.updated_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                    </p>
                  </div>
                  <Badge tone="signal">Review</Badge>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {can('can_manage_leads') && data.leads && (
          <Card>
            <div className="flex items-start justify-between">
              <h2 className="text-base font-semibold text-ink">Lead pipeline</h2>
              <Link to="/admin/leads" className="text-sm font-medium no-underline hover:underline">
                Open leads →
              </Link>
            </div>
            <div className="mt-5">
              <Sparkline points={data.leads.last_30_days} label="New leads" />
            </div>
            <dl className="mt-5 grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
              {[
                { label: 'New', value: data.leads.new },
                { label: 'Open', value: data.leads.open },
                { label: 'Unassigned', value: data.leads.unassigned },
                { label: 'Assigned to you', value: data.leads.mine },
              ].map((item) => (
                <div key={item.label}>
                  <dt className="text-xs text-muted">{item.label}</dt>
                  <dd className="mt-0.5 font-display text-xl font-bold text-ink">{item.value}</dd>
                </div>
              ))}
            </dl>
          </Card>
        )}

        {can('can_manage_applications') && data.applications && (
          <Card>
            <div className="flex items-start justify-between">
              <h2 className="text-base font-semibold text-ink">Job applications</h2>
              <Link to="/admin/careers/applications" className="text-sm font-medium no-underline hover:underline">
                Review →
              </Link>
            </div>
            <dl className="mt-5 grid grid-cols-2 gap-4 text-sm">
              <div>
                <dt className="text-xs text-muted">Total received</dt>
                <dd className="mt-0.5 font-display text-2xl font-bold text-ink">{data.applications.total}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted">Not yet screened</dt>
                <dd className="mt-0.5 font-display text-2xl font-bold text-signal">{data.applications.new}</dd>
              </div>
            </dl>
          </Card>
        )}

        <Card>
          <h2 className="text-base font-semibold text-ink">Continue where you left off</h2>
          {myContentQuery.isLoading ? (
            <div className="mt-4">
              <Spinner />
            </div>
          ) : (myContentQuery.data?.items.length ?? 0) === 0 ? (
            <p className="mt-4 text-sm text-muted">You have not edited anything yet.</p>
          ) : (
            <ul className="mt-4 divide-y divide-line">
              {myContentQuery.data!.items.map((item) => (
                <li key={`${item.type}-${item.id}`} className="flex items-center justify-between gap-4 py-3">
                  <Link to={item.admin_path} className="min-w-0 flex-1 truncate text-sm font-medium no-underline hover:underline">
                    {item.title}
                  </Link>
                  <Badge tone={item.status === 'published' ? 'success' : item.status === 'review' ? 'signal' : 'neutral'}>
                    {item.status}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="lg:col-span-2">
          <div className="flex items-start justify-between">
            <h2 className="text-base font-semibold text-ink">Recent activity</h2>
            {can('can_view_audit') && (
              <Link to="/admin/audit" className="text-sm font-medium no-underline hover:underline">
                Full audit log →
              </Link>
            )}
          </div>
          {data.recent_activity.length === 0 ? (
            <EmptyState title="No activity recorded yet" description="Actions will appear here as content is created and published." />
          ) : (
            <ul className="mt-4 divide-y divide-line">
              {data.recent_activity.map((entry) => (
                <li key={entry.id} className="flex items-start gap-3 py-3">
                  <Icon name="activity" className="mt-0.5 h-4 w-4 shrink-0 text-muted" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-ink">
                      <span className="font-medium">{entry.actor_label}</span> {entry.action_label.toLowerCase()}{' '}
                      {entry.object_repr && <span className="text-muted">{entry.object_repr}</span>}
                    </p>
                    <p className="mt-0.5 text-xs text-muted">
                      {new Date(entry.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
